import { useRef } from 'react'
import { productTotal, type CartItem } from '@/services/shopService'

interface ShopCartProps {
  items: CartItem[]
  userCoins: number
  busy: boolean
  onRemove: (productId: string) => void
  onSizeChange: (productId: string, size: string) => void
  onCheckout: () => void
}

export default function ShopCart({
  items,
  userCoins,
  busy,
  onRemove,
  onSizeChange,
  onCheckout,
}: ShopCartProps) {
  const heading = useRef<HTMLHeadingElement>(null)
  const total = items.reduce((sum, { product }) => sum + productTotal(product), 0)
  const shipping = items.reduce(
    (sum, { product }) => sum + (product.requiresDelivery ? product.shippingCost : 0),
    0,
  )
  const missing = Math.max(0, total - userCoins)

  return (
    <section className="ac-cart" aria-labelledby="shop-cart-title">
      <h2 id="shop-cart-title" tabIndex={-1} ref={heading}>
        Tvůj košík <span>· {items.length}</span>
      </h2>
      {items.length === 0 ? (
        <p>Košík je prázdný. Vyber si něco z nabídky a přidej to sem.</p>
      ) : (
        <>
          <ul className="ac-cart-items">
            {items.map(({ product, size }) => (
              <li key={product.id}>
                <div className="ac-cart-product">
                  <h3>{product.name}</h3>
                  {product.sizes.length > 0 && (
                    <label htmlFor={`cart-size-${product.id}`}>
                      Varianta
                      <select
                        id={`cart-size-${product.id}`}
                        value={size}
                        disabled={busy}
                        onChange={(event) => onSizeChange(product.id, event.target.value)}
                      >
                        {product.sizes.map((variant) => (
                          <option key={variant}>{variant}</option>
                        ))}
                      </select>
                    </label>
                  )}
                </div>
                <div className="ac-cart-line-price">
                  <strong>{productTotal(product).toLocaleString('cs-CZ')} mincí</strong>
                  {product.requiresDelivery && (
                    <small>z toho doprava {product.shippingCost.toLocaleString('cs-CZ')}</small>
                  )}
                </div>
                <button
                  type="button"
                  className="ac-shop-button ac-shop-button-secondary"
                  disabled={busy}
                  aria-label={`Odebrat ${product.name}`}
                  onClick={() => {
                    onRemove(product.id)
                    heading.current?.focus()
                  }}
                >
                  Odebrat
                </button>
              </li>
            ))}
          </ul>
          <div className="ac-cart-summary">
            <div>
              <p>
                Celkem <strong>{total.toLocaleString('cs-CZ')} mincí</strong>
              </p>
              <small>
                {shipping > 0
                  ? `Včetně ${shipping.toLocaleString('cs-CZ')} mincí za dopravu. Doprava se sčítá za jednotlivé produkty.`
                  : 'Bez poplatku za dopravu.'}
              </small>
              <small>Dodací údaje vyplníš až v dalším kroku, pokud je potřebujeme.</small>
              {missing > 0 && (
                <p role="status">Na celý košík ti chybí {missing.toLocaleString('cs-CZ')} mincí.</p>
              )}
            </div>
            <button
              type="button"
              className="ac-shop-button"
              disabled={busy || missing > 0}
              onClick={onCheckout}
            >
              Pokračovat k objednávce
            </button>
          </div>
        </>
      )}
    </section>
  )
}
